import styled from "styled-components"
import Logo from "@/components/Menu/shared/Logo"
import ToggleMenuButton from "@/components/Menu/shared/ToggleMenuButton"
import { useSelector } from "react-redux"
import { getCssSizeValue } from "@repo/utils"
import { BREAKPOINTS } from "@repo/ui/themeSettings"
import { useRouter } from "next/router"
import { CURSOR_TYPES } from "@repo/utils/constants"
import useSpecifyColor from "@repo/ui/src/hooks/useSpecifyColor"
import { useIsMobile } from "@repo/ui/src/providers/DeviceProvider"
import StoryButton from "@repo/ui/src/components/common/StoryButton"
import LetsTalkButton from "@repo/ui/src/components/common/LetsTalkButton"
import { useScrollContext } from "@repo/ui/src/components/Scroll"
import useHideTopBarOnScroll from "@repo/ui/src/hooks/useHideTopBarOnScroll"
import useSetIsMenuTransparentOnPageLoad from "@repo/ui/src/hooks/useSetIsMenuTransparentOnPageLoad"

const StyledTopRow = styled.div`
	z-index: 99;
	align-items: center;
	display: flex;
	justify-content: space-between;
	width: 100%;
	padding-top: ${getCssSizeValue(15, BREAKPOINTS.MOBILE)};
	padding-bottom: ${getCssSizeValue(15, BREAKPOINTS.MOBILE)};
	position: fixed;
	transition: all var(--color-transition-duration) var(--ease);
	transform: translateY(-100%);
	will-change: transform, opacity;

	&.menu-small {
		padding-top: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
		padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
	}

	&.open {
		background: transparent;
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet}px) {
		padding-top: ${getCssSizeValue(25, BREAKPOINTS.TABLET)};
		padding-bottom: ${getCssSizeValue(15, BREAKPOINTS.TABLET)};

		&.menu-small {
			padding-top: ${getCssSizeValue(10, BREAKPOINTS.TABLET)};
			padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.TABLET)};
		}
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		padding-top: ${getCssSizeValue(45, BREAKPOINTS.DESKTOP)};
		padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};

		&.menu-small {
			padding-top: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
			padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
		}
	}
`

const StyledTopRowBackground = styled.div`
	position: absolute;
	z-index: 1;
	inset: 0;
	transition: all var(--color-transition-duration) var(--ease);
	background: var(--primary-color);
	opacity: 1;
	will-change: opacity, background;

	&.transparent {
		opacity: 0;
	}
`

const StyledRightPartWrapper = styled.div`
	position: relative;
	z-index: 10;
	display: flex;
	gap: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
	margin-right: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};

	.story-button {
		margin-right: 50px;
		z-index: 10;
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet}px) {
		.story-button {
			margin-right: 6px;
		}
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		.story-button {
			margin-right: unset;
		}

		margin-right: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
	}
`

const StyledContactButtonWrapper = styled.div`
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet}px) {
		opacity: 0;
		transition: opacity var(--color-transition-duration) var(--ease);
		margin-right: ${getCssSizeValue(65, BREAKPOINTS.TABLET)};
		pointer-events: none;

		&.visible {
			opacity: 1;
			visibility: visible;
			pointer-events: auto;
		}
	}
`

const StyledLogoWrapper = styled.div`
	z-index: 1000;
`

const TopRow = ({ firstMenuItem, contactButtonText, contactButtonLink }) => {
	const router = useRouter()
	const { open, isMenuTransparent } = useSelector((state) => state.menu)
	const bgColor = useSpecifyColor()
	const contactButtonLinkSlug = contactButtonLink ? `/${contactButtonLink?.slug}` : ""
	const {
		instance: { __lenis },
	} = useScrollContext()
	const { menuTransition } = useSelector((state) => state.menu)
	const isMobile = useIsMobile()

	const { isMenuSmall } = useHideTopBarOnScroll()
	useSetIsMenuTransparentOnPageLoad()

	const handleContactClick = () => {
		if (router.asPath.includes("/contact")) {
			__lenis.scrollTo(0)
		} else {
			router.push(contactButtonLinkSlug, contactButtonLinkSlug, { scroll: false })
		}
	}

	return (
		<StyledTopRow
			data-cursor={CURSOR_TYPES.HTML}
			$bgColor={bgColor}
			id="menu-top-row"
			className={`menu-top-row px-main ${menuTransition ? "open" : ""} ${!open && isMenuSmall ? "menu-small" : ""}`}
		>
			<StyledTopRowBackground className={`top-row-background ${isMenuTransparent ? "transparent" : ""}`} />
			<StyledLogoWrapper>
				<Logo firstMenuItem={firstMenuItem} />
			</StyledLogoWrapper>
			<StyledRightPartWrapper>
				<StoryButton isDisabled />
				{!isMobile && (
					<StyledContactButtonWrapper className={`${!open ? "visible" : ""}`}>
						<LetsTalkButton onClick={handleContactClick} pleo="design">
							{contactButtonText}
						</LetsTalkButton>
					</StyledContactButtonWrapper>
				)}
				<ToggleMenuButton />
			</StyledRightPartWrapper>
		</StyledTopRow>
	)
}

export default TopRow
